export function rectCenter(rect) {
  return {
    x: rect.x + rect.width / 2,
    y: rect.y + rect.height / 2,
  };
}

export function rectBoundaryAnchor(rect, toward) {
  const center = rectCenter(rect);
  const dx = toward.x - center.x;
  const dy = toward.y - center.y;

  if (dx === 0 && dy === 0) return center;

  const halfWidth = Math.max(rect.width / 2, 0.5);
  const halfHeight = Math.max(rect.height / 2, 0.5);
  const scale = 1 / Math.max(Math.abs(dx) / halfWidth, Math.abs(dy) / halfHeight);

  return {
    x: center.x + dx * scale,
    y: center.y + dy * scale,
  };
}

export function rectSideAnchor(rect, side) {
  const center = rectCenter(rect);
  if (side === 'left') return { x: rect.x, y: center.y };
  if (side === 'right') return { x: rect.x + rect.width, y: center.y };
  if (side === 'top') return { x: center.x, y: rect.y };
  if (side === 'bottom') return { x: center.x, y: rect.y + rect.height };
  throw new TypeError(`Unsupported rectangle side: ${side}`);
}

const SIDES = new Set(['left', 'right', 'top', 'bottom']);

function oppositeSide(side) {
  if (side === 'left') return 'right';
  if (side === 'right') return 'left';
  if (side === 'top') return 'bottom';
  return 'top';
}

export function connectionSide(fromRect, toRect) {
  const fromCenter = rectCenter(fromRect);
  const toCenter = rectCenter(toRect);
  const dx = toCenter.x - fromCenter.x;
  const dy = toCenter.y - fromCenter.y;

  if (dx === 0 && dy === 0) return 'right';

  if (Math.abs(dx) >= Math.abs(dy)) {
    return dx < 0 ? 'left' : 'right';
  }
  return dy < 0 ? 'top' : 'bottom';
}

export function routeBoundaryCurve(fromRect, toRect, options = {}) {
  const side = SIDES.has(options.side)
    ? options.side
    : connectionSide(fromRect, toRect);
  const from = rectSideAnchor(fromRect, side);
  const to = rectSideAnchor(toRect, oppositeSide(side));
  const dx = to.x - from.x;
  const dy = to.y - from.y;

  let control1;
  let control2;
  if (side === 'left' || side === 'right') {
    const bend = Math.max(32, Math.min(Math.abs(dx) * 0.5, 220));
    const direction = side === 'left' ? -1 : 1;
    control1 = { x: from.x + bend * direction, y: from.y };
    control2 = { x: to.x - bend * direction, y: to.y };
  } else {
    const bend = Math.max(32, Math.min(Math.abs(dy) * 0.5, 220));
    const direction = side === 'top' ? -1 : 1;
    control1 = { x: from.x, y: from.y + bend * direction };
    control2 = { x: to.x, y: to.y - bend * direction };
  }

  return {
    side,
    from,
    to,
    control1,
    control2,
    path: `M ${from.x} ${from.y} C ${control1.x} ${control1.y}, ${control2.x} ${control2.y}, ${to.x} ${to.y}`,
  };
}
